import React, { useState, useEffect } from 'react';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon } from '../../services/api';
import { Coupon } from '../../types';
import { EditIcon, TrashIcon, PlusIcon, XIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { validateTextInput, validateNumeric, validateCouponCode } from '../../utils/validation';
import SingleDatePicker from '../../components/SingleDatePicker';

const CouponsManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: '',
    min_amount: '',
    max_discount: '',
    usage_limit: '',
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const data = await getCoupons();
      setCoupons(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setCoupons([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Coupon name is required');
      setLoading(false);
      return;
    }
    if (!formData.code.trim()) {
      setError('Coupon code is required');
      setLoading(false);
      return;
    }
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      setError('Discount value must be greater than 0');
      setLoading(false);
      return;
    }
    if (!['percentage', 'fixed'].includes(formData.discount_type)) {
      setError('Invalid discount type');
      setLoading(false);
      return;
    }

    try {
      const couponData = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        min_amount: formData.min_amount ? parseFloat(formData.min_amount) : undefined,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : undefined,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : undefined,
        expires_at: formData.expires_at ? new Date(formData.expires_at + 'T12:00:00').toISOString() : undefined,
        is_active: formData.is_active
      };


      if (editingCoupon) {
        await updateCoupon(editingCoupon.coupon_id, couponData);
      } else {
        await createCoupon(couponData);
      }

      fetchCoupons();
      setShowForm(false);
      setEditingCoupon(null);
      setFormData({
        name: '',
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_amount: '',
        max_discount: '',
        usage_limit: '',
        expires_at: '',
        is_active: true
      });
    } catch (error: any) {
      setError(`Failed to save coupon: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (coupon: Coupon) => {
    console.log('DEBUG: handleEdit called with coupon:', coupon);
    setEditingCoupon(coupon);
    const formData = {
      name: coupon.name || '',
      code: coupon.code || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value?.toString() || '',
      min_amount: coupon.min_amount?.toString() || '',
      max_discount: coupon.max_discount?.toString() || '',
      usage_limit: coupon.usage_limit?.toString() || '',
      expires_at: coupon.expires_at || '',
      is_active: coupon.is_active ?? true
    };
    setFormData(formData);
    setShowForm(true);
  };

  const handleDelete = async (couponId: string) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      await deleteCoupon(couponId);
      fetchCoupons();
    } catch (error) {
      setError('Failed to delete coupon');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCoupon(null);
    setFormData({
      name: '',
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_amount: '',
      max_discount: '',
      usage_limit: '',
      expires_at: '',
      is_active: true
    });
  };

  const toggleRowExpanded = (couponId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h3 className="text-lg font-medium text-gray-900">Coupons Management</h3>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Coupon
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Code
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SAVE20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Type
                </label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'percentage' | 'fixed' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Value
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === 'percentage' ? '20' : '20.00'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.min_amount}
                  onChange={(e) => setFormData({ ...formData, min_amount: e.target.value })}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Discount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  placeholder="Optional (for % discounts)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usage Limit
                </label>
                <input
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date
                </label>
                <SingleDatePicker
                  value={formData.expires_at}
                  onChange={(date) => setFormData({ ...formData, expires_at: date })}
                  minDate={new Date().toISOString().split('T')[0]}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Coupon'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {Array.isArray(coupons) && coupons.map((coupon) => (
          <div key={coupon.coupon_id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 truncate">{coupon.name}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                    {coupon.code}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                    coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {coupon.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount:</span>
                <span className="font-medium text-gray-900">
                  {coupon.discount_type === 'percentage' 
                    ? `${parseFloat(String(coupon.discount_value || 0))}%` 
                    : `$${parseFloat(String(coupon.discount_value || 0)).toFixed(2)}`
                  }
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Usage:</span>
                <span className="text-gray-900">
                  {coupon.usage_count} {coupon.usage_limit ? `/ ${coupon.usage_limit}` : ''}
                </span>
              </div>
              {coupon.expires_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Expires:</span>
                  <span className="text-gray-900">{new Date(coupon.expires_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <button
                onClick={() => toggleRowExpanded(coupon.coupon_id)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                {expandedRows.has(coupon.coupon_id) ? (
                  <>
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronRightIcon className="h-4 w-4 mr-1" />
                    View Details
                  </>
                )}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(coupon)}
                  className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded"
                  title="Edit"
                >
                  <EditIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(coupon.coupon_id)}
                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {expandedRows.has(coupon.coupon_id) && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Discount Type</h5>
                  <p className="text-xs text-gray-600 capitalize">{coupon.discount_type}</p>
                </div>
                {coupon.min_amount && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-1">Min Purchase</h5>
                    <p className="text-xs text-gray-600">${parseFloat(String(coupon.min_amount || 0)).toFixed(2)}</p>
                  </div>
                )}
                {coupon.max_discount && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-1">Max Discount</h5>
                    <p className="text-xs text-gray-600">${parseFloat(String(coupon.max_discount || 0)).toFixed(2)}</p>
                  </div>
                )}
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Coupon ID</h5>
                  <p className="text-xs text-gray-500 font-mono break-all">{coupon.coupon_id}</p>
                </div>
              </div>
            )}
          </div>
        ))}
        {coupons.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
            No coupons found
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Coupon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Discount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.isArray(coupons) && coupons.map((coupon) => (
              <React.Fragment key={coupon.coupon_id}>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{coupon.name}</div>
                    <div className="text-sm text-gray-500">
                      {coupon.expires_at ? `Expires: ${new Date(coupon.expires_at).toLocaleDateString()}` : 'No expiration'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                    {coupon.code}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {coupon.discount_type === 'percentage' 
                      ? `${parseFloat(String(coupon.discount_value || 0))}%` 
                      : `$${parseFloat(String(coupon.discount_value || 0)).toFixed(2)}`
                    }
                    {coupon.min_amount && <div className="text-xs text-gray-500">Min: $${parseFloat(String(coupon.min_amount || 0)).toFixed(2)}</div>}
                    {coupon.max_discount && <div className="text-xs text-gray-500">Max: $${parseFloat(String(coupon.max_discount || 0)).toFixed(2)}</div>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {coupon.usage_count} {coupon.usage_limit ? `/ ${coupon.usage_limit}` : ''}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {coupon.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleRowExpanded(coupon.coupon_id)}
                      className="text-gray-600 hover:text-gray-900"
                      title="View Details"
                    >
                      {expandedRows.has(coupon.coupon_id) ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(coupon)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit"
                    >
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(coupon.coupon_id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
              {expandedRows.has(coupon.coupon_id) && (
                <tr>
                  <td colSpan={6} className="px-6 py-4 bg-gray-50">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Coupon ID</h4>
                          <p className="text-xs text-gray-500 font-mono">{coupon.coupon_id}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Discount Type</h4>
                          <p className="text-sm text-gray-600 capitalize">{coupon.discount_type}</p>
                        </div>
                      </div>
                      {coupon.min_amount && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Minimum Purchase Amount</h4>
                          <p className="text-sm text-gray-600">${parseFloat(String(coupon.min_amount || 0)).toFixed(2)}</p>
                        </div>
                      )}
                      {coupon.max_discount && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Maximum Discount</h4>
                          <p className="text-sm text-gray-600">${parseFloat(String(coupon.max_discount || 0)).toFixed(2)}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Usage</h4>
                          <p className="text-sm text-gray-600">
                            {coupon.usage_count} used {coupon.usage_limit ? `(limit: ${coupon.usage_limit})` : '(unlimited)'}
                          </p>
                        </div>
                        {coupon.expires_at && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Expiration</h4>
                            <p className="text-sm text-gray-600">{new Date(coupon.expires_at).toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default CouponsManagement;
